import type { JsonObject } from "@inf-monkeys-tech/monkeys";
import type { CSSProperties } from "react";

import { cn } from "../base";

export type RecordHistoryDagRow = {
  commitId: string;
  lane: number;
  lanesIn: number[];
  lanesOut: number[];
  edgesDown: Array<{
    fromLane: number;
    toLane: number;
    parentCommitId: string;
  }>;
  onMainLine: boolean;
  mergesIntoLane?: number;
};

export type RecordHistoryDagLayout = {
  rows: RecordHistoryDagRow[];
  laneCount: number;
};

const text = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const parentIds = (commit: JsonObject | undefined): string[] =>
  Array.isArray(commit?.parentCommitIds)
    ? commit.parentCommitIds.map(text).filter(Boolean)
    : [];

/**
 * Compute the product-neutral Git-style history rail used by record history.
 * Commits are newest first; the active head and its first-parent ancestors
 * stay in lane zero while unreachable revisions remain visible on side lanes.
 */
export const computeRecordHistoryDag = (
  commits: JsonObject[],
  headCommitId: string,
): RecordHistoryDagLayout => {
  if (!commits.length) return { rows: [], laneCount: 0 };

  const indexByCommit = new Map<string, number>();
  commits.forEach((commit, index) => {
    const commitId = text(commit.commitId);
    if (commitId) indexByCommit.set(commitId, index);
  });
  const resolvedHead =
    headCommitId && indexByCommit.has(headCommitId)
      ? headCommitId
      : text(commits[0]?.commitId);
  const mainLine = new Set<string>();
  let cursor = resolvedHead;
  while (cursor && !mainLine.has(cursor)) {
    mainLine.add(cursor);
    const index = indexByCommit.get(cursor);
    if (index === undefined) break;
    cursor = parentIds(commits[index]).find((parent) =>
      indexByCommit.has(parent),
    ) ?? "";
  }

  const occupiedLanes = [-1];
  const laneByCommit = new Map<string, number>();
  const acquireLane = () => {
    for (let lane = 1; lane < occupiedLanes.length; lane += 1) {
      if (occupiedLanes[lane] === -1) return lane;
    }
    occupiedLanes.push(-1);
    return occupiedLanes.length - 1;
  };
  commits.forEach((commit, index) => {
    const commitId = text(commit.commitId);
    if (!commitId) return;
    if (mainLine.has(commitId)) {
      laneByCommit.set(commitId, 0);
      occupiedLanes[0] = index;
      return;
    }
    let lane: number | undefined;
    for (let childIndex = index - 1; childIndex >= 0; childIndex -= 1) {
      if (parentIds(commits[childIndex])[0] !== commitId) continue;
      const childLane = laneByCommit.get(text(commits[childIndex]?.commitId));
      if (childLane !== undefined && childLane !== 0) {
        lane = childLane;
        break;
      }
    }
    lane ??= acquireLane();
    laneByCommit.set(commitId, lane);
    occupiedLanes[lane] = index;
  });

  const laneSpans = new Map<number, { top: number; bottom: number }>();
  commits.forEach((commit, index) => {
    const lane = laneByCommit.get(text(commit.commitId));
    if (lane === undefined) return;
    const span = laneSpans.get(lane);
    if (span) {
      span.top = Math.min(span.top, index);
      span.bottom = Math.max(span.bottom, index);
    } else {
      laneSpans.set(lane, { top: index, bottom: index });
    }
  });
  laneSpans.set(0, { top: 0, bottom: commits.length - 1 });

  const mergesInto = new Map<number, number>();
  commits.forEach((commit, index) => {
    const commitId = text(commit.commitId);
    const lane = laneByCommit.get(commitId);
    if (!commitId || lane === undefined || mainLine.has(commitId)) return;
    const knownParent = parentIds(commit).find((parent) =>
      indexByCommit.has(parent),
    );
    if (knownParent && !mainLine.has(knownParent)) return;
    const bottom = commits.reduce(
      (candidate, other, otherIndex) =>
        otherIndex > index &&
        laneByCommit.get(text(other.commitId)) === lane
          ? otherIndex
          : candidate,
      index,
    );
    if (bottom !== index) return;
    if (knownParent) {
      mergesInto.set(index, laneByCommit.get(knownParent) ?? 0);
      return;
    }
    const nextMainIndex = commits.findIndex(
      (other, otherIndex) =>
        otherIndex > index && mainLine.has(text(other.commitId)),
    );
    if (nextMainIndex >= 0) {
      mergesInto.set(
        index,
        laneByCommit.get(text(commits[nextMainIndex]?.commitId)) ?? 0,
      );
    }
  });

  const rows = commits.flatMap((commit, index) => {
    const commitId = text(commit.commitId);
    const lane = laneByCommit.get(commitId);
    if (!commitId || lane === undefined) return [];
    const lanesIn: number[] = [];
    const lanesOut: number[] = [];
    laneSpans.forEach((span, candidateLane) => {
      if (index > span.top && index <= span.bottom)
        lanesIn.push(candidateLane);
      if (index >= span.top && index < span.bottom)
        lanesOut.push(candidateLane);
    });
    const edgesDown = parentIds(commit).flatMap((parentCommitId) => {
      const parentLane = laneByCommit.get(parentCommitId);
      return parentLane === undefined
        ? []
        : [{ fromLane: lane, toLane: parentLane, parentCommitId }];
    });
    const mergeLane = mergesInto.get(index);
    if (
      mergeLane !== undefined &&
      mergeLane !== lane &&
      !edgesDown.some(
        (edge) => edge.fromLane === lane && edge.toLane === mergeLane,
      )
    ) {
      edgesDown.push({
        fromLane: lane,
        toLane: mergeLane,
        parentCommitId: "",
      });
    }
    return [
      {
        commitId,
        lane,
        lanesIn: Array.from(new Set(lanesIn)).sort((a, b) => a - b),
        lanesOut: Array.from(new Set(lanesOut)).sort((a, b) => a - b),
        edgesDown,
        onMainLine: mainLine.has(commitId),
        ...(mergeLane === undefined ? {} : { mergesIntoLane: mergeLane }),
      },
    ];
  });

  return {
    rows,
    laneCount: Math.max(
      1,
      ...Array.from(laneSpans.keys()).map((lane) => lane + 1),
    ),
  };
};

const LANE_PX = 18;
const NODE_SIZE_PX = 11;

export const RecordHistoryDagRail = ({
  row,
  laneCount,
  selected,
  current,
  rollback,
}: {
  row: RecordHistoryDagRow;
  laneCount: number;
  selected: boolean;
  current: boolean;
  rollback: boolean;
}) => {
  const width = Math.max(laneCount, row.lane + 1, 1) * LANE_PX;
  const nodeCenter = row.lane * LANE_PX + LANE_PX / 2;
  const vertical = (
    lane: number,
    half: "top" | "bottom",
    key: string,
  ) => {
    const style: CSSProperties = { left: lane * LANE_PX + LANE_PX / 2 - 0.5 };
    if (half === "top") {
      style.top = 0;
      style.height = "50%";
    } else {
      style.top = "50%";
      style.bottom = 0;
    }
    return (
      <span
        key={key}
        aria-hidden
        className={cn(
          "absolute w-px",
          lane === row.lane && row.onMainLine
            ? "bg-success/70"
            : "bg-muted-foreground/30",
        )}
        style={style}
      />
    );
  };
  const straightLanes = row.edgesDown
    .filter((edge) => edge.fromLane === edge.toLane)
    .map((edge) => edge.fromLane);
  const bottomLanes = Array.from(
    new Set([...row.lanesOut, ...straightLanes]),
  ).sort((a, b) => a - b);
  return (
    <div
      className="relative shrink-0 self-stretch"
      style={{ width }}
      aria-hidden
      data-record-history-dag-lane={row.lane}
    >
      {row.lanesIn.map((lane) => vertical(lane, "top", `in-${lane}`))}
      {bottomLanes.map((lane) => vertical(lane, "bottom", `out-${lane}`))}
      {row.edgesDown.length ? (
        <svg
          className="absolute inset-x-0 overflow-visible"
          style={{ top: "50%", bottom: 0, height: "50%" }}
          width={width}
          viewBox={`0 0 ${width} 100`}
          preserveAspectRatio="none"
        >
          {row.edgesDown.map((edge, index) => {
            const from = edge.fromLane * LANE_PX + LANE_PX / 2;
            const to = edge.toLane * LANE_PX + LANE_PX / 2;
            return edge.fromLane === edge.toLane ? (
              <line
                key={`edge-${index}`}
                x1={from}
                x2={to}
                y1={0}
                y2={100}
                className={cn(
                  "stroke-[1.5]",
                  edge.fromLane === row.lane && row.onMainLine
                    ? "stroke-success/70"
                    : "stroke-muted-foreground/30",
                )}
              />
            ) : (
              <path
                key={`edge-${index}`}
                d={`M ${from} 0 C ${from} 60, ${to} 40, ${to} 100`}
                className={cn(
                  "fill-none stroke-[1.5]",
                  rollback ? "stroke-warning" : "stroke-muted-foreground/60",
                )}
              />
            );
          })}
        </svg>
      ) : null}
      <span
        className={cn(
          "absolute rounded-full border-2 transition-colors",
          current && "border-success/25 bg-success",
          !current && selected && "border-primary bg-primary",
          !current && !selected && rollback && "border-warning/30 bg-warning",
          !current && !selected && !rollback && row.onMainLine &&
            "border-success/25 bg-background",
          !current && !selected && !rollback && !row.onMainLine &&
            "border-muted-foreground/50 bg-background",
        )}
        style={{
          left: nodeCenter - NODE_SIZE_PX / 2,
          top: `calc(50% - ${NODE_SIZE_PX / 2}px)`,
          width: NODE_SIZE_PX,
          height: NODE_SIZE_PX,
        }}
      />
    </div>
  );
};
